/**
 * "Abrir" o arquivo recebido antes de decidir o vínculo (E177.F4.C02, F-E177.F4-02; GUS-253: mesmo
 * nome, conteúdo diferente, conseguir abrir para conferir). Baixa o original pelo id do documento,
 * na sessão de quem está na tela, pela mesma rota da aba Documentos
 * (`GET /api/casos/{id}/documents/{doc}/arquivo`): o servidor confere que o documento é do caso, e o
 * arquivo guardado que não confere com o recebimento não desce.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { ptBR } from '@/i18n/pt-BR';
import { DocumentError, baixarArquivoOriginal } from '@/lib/case_source_documents';
import { salvarArquivoBaixado } from '@/lib/http_download';

import { LINK_DA_CCB } from './LinhasDaCcb';

const t = ptBR.abertura.contratosDigitais;

export interface AbrirArquivoProps {
  client: AxiosInstance;
  caseId: string;
  documentoId: string;
  /** O nome como a tela o mostra (com o identificador, no homônimo). */
  nome: string;
}

export function AbrirArquivo({
  client,
  caseId,
  documentoId,
  nome,
}: AbrirArquivoProps): JSX.Element {
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function abrir(): Promise<void> {
    setAbrindo(true);
    setErro(null);
    try {
      salvarArquivoBaixado(await baixarArquivoOriginal(client, caseId, documentoId, nome));
    } catch (err) {
      const divergente = err instanceof DocumentError && err.reason === 'original_divergente';
      setErro(divergente ? ptBR.portal.resultado.erroOriginalDivergente : t.erroAoAbrir);
    } finally {
      setAbrindo(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={LINK_DA_CCB}
        onClick={() => void abrir()}
        disabled={abrindo}
        aria-label={t.abrirArquivo.replace('{arquivo}', nome)}
        data-testid={`ccb-abrir-${documentoId}`}
      >
        {t.abrir}
      </button>
      {erro !== null ? (
        <span role="alert" className="text-[13px] text-danger">
          {erro}
        </span>
      ) : null}
    </>
  );
}
